import {
  POST_EDITOR_BLOCK_INDENT_TYPES,
  POST_EDITOR_MIN_INDENT,
} from './post_editor_constants.js';
import { indentFromNode } from './post_block_indent.js';

const LIST_TYPES = new Set(['bulletList', 'orderedList']);

// Block-indent types we strip indent from when they live inside a list item.
// Nested lists are intentionally excluded — they are independent containers
// and get normalized as their own top-level lists elsewhere.
const LIST_ITEM_INDENT_CLEAR_TYPES = new Set(
  POST_EDITOR_BLOCK_INDENT_TYPES.filter((t) => !LIST_TYPES.has(t))
);

function paragraphIndent(node) {
  return indentFromNode(node);
}

function blockIndentInListItem(child) {
  if (child.type.name === 'paragraph') return paragraphIndent(child);
  if (POST_EDITOR_BLOCK_INDENT_TYPES.includes(child.type.name)) return indentFromNode(child);
  return POST_EDITOR_MIN_INDENT;
}

/** Indent level to apply on a list when wrapping indented paragraph(s). */
export function resolveListIndentFromSelection(state) {
  const { from, to } = state.selection;
  let maxIndent = POST_EDITOR_MIN_INDENT;

  const consider = (node) => {
    const level = paragraphIndent(node);
    if (level > maxIndent) maxIndent = level;
  };

  state.doc.nodesBetween(from, to, (node) => {
    if (node.type.name === 'paragraph') consider(node);
  });

  if (maxIndent <= POST_EDITOR_MIN_INDENT) {
    const { $from } = state.selection;
    for (let d = $from.depth; d > 0; d--) {
      if ($from.node(d).type.name === 'paragraph') {
        consider($from.node(d));
        break;
      }
    }
  }

  return maxIndent;
}

export function listAttrsForIndent(state) {
  const indent = resolveListIndentFromSelection(state);
  return indent > POST_EDITOR_MIN_INDENT ? { indent } : {};
}

/**
 * Move visual indent from paragraphs inside list items onto the list node.
 * Markers and text stay aligned (indent on <ul>/<ol>, not inner <p>).
 */
export function normalizeIndentedListItemsInRange(tr, from, to) {
  const lists = [];

  tr.doc.nodesBetween(from, to, (node, pos) => {
    if (!LIST_TYPES.has(node.type.name)) return;
    lists.push({ node, pos });
    return false;
  });

  let modified = false;

  lists.forEach(({ node, pos }) => {
    let listIndent = paragraphIndent(node);

    node.forEach((listItem) => {
      listItem.forEach((child) => {
        const level = blockIndentInListItem(child);
        if (level > listIndent) listIndent = level;
      });
    });

    if (listIndent > POST_EDITOR_MIN_INDENT && (node.attrs.indent || 0) !== listIndent) {
      tr.setNodeMarkup(pos, null, { ...node.attrs, indent: listIndent });
      modified = true;
    }

    node.forEach((listItem, liOffset) => {
      // PM's Fragment.forEach passes (child, offset). `childOffset` is the
      // positional offset (sum of preceding child nodeSizes) — exactly
      // what we need to compute the doc position. Inside a list item the
      // visual indent always lives on the list node, so any block-indent
      // child carrying its own `indent` produces a double margin-left.
      listItem.forEach((child, childOffset) => {
        if (!LIST_ITEM_INDENT_CLEAR_TYPES.has(child.type.name)) return;
        const childIndent = indentFromNode(child);
        if (childIndent <= POST_EDITOR_MIN_INDENT) return;

        const childPos = pos + 1 + liOffset + 1 + childOffset;
        tr.setNodeMarkup(childPos, null, {
          ...child.attrs,
          indent: POST_EDITOR_MIN_INDENT,
        });
        modified = true;
      });
    });
  });

  return modified;
}
